import { createSelector, createSlice } from '@reduxjs/toolkit';
import { get, isEqual, pick } from 'lodash';

import { toFilter } from '~/components/SearchV2/SearchV2.utils';
import { SEARCH_COUNT_SINGLE_VALUE_ID } from '~/components/Shared/Charts/constants';
import { SliceType } from '~/enums/SliceType';
import { SearchDocumentItem } from '~/features/advanced-search/types';
import { buildFilterQuery } from '~/features/advanced-search/utils/build-query';
import { SavedSearch } from '~/features/search/types/search';
import { testIsQueryV3 } from '~/features/search/utils/queryUtils';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { SearchItem } from '~/redux/api/methods/evisearch';
import { Chart } from '~/types/DashboardV2';
import { DropdownFieldValue } from '~/types/Field';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

import { toSearchV3Filters } from '../../api/methods/searchV3';
import { getPersistedState } from '../../persist';
import { isActiveFilter } from '../../slices/search';
import {
  ColumnWidths,
  InitData,
  InitSavedSearch,
  KeyMapToPersist,
  SearchV3Slice,
} from './types';

const name = SliceType.SearchV3;

const keysToPersist: (keyof SearchV3Slice)[] = ['columnWidths'];

const keyMapToPersist: KeyMapToPersist = keysToPersist.reduce<KeyMapToPersist>(
  (prev, curr) => {
    prev[curr] = [name, curr];
    return prev;
  },
  {},
);
export const pathsToPersist = Object.values(keyMapToPersist);

const persistedState = {
  columnWidths: getPersistedState(keyMapToPersist.columnWidths!, {}),
};

export const getNonFilterCharts = (
  dashboard: Dashboard,
): Record<string, Chart> => ({
  [SEARCH_COUNT_SINGLE_VALUE_ID]: {
    id: SEARCH_COUNT_SINGLE_VALUE_ID,
    label: dashboard.name,
    section: 'General Information',
    type: 'single_value',
  },
});

export const initialState: SearchV3Slice = {
  columnOrder: [],
  fieldValues: {},
  intervals: {},
  chartWidths: {},
  page: 1,
  pageSize: 20,
  searchText: undefined,
  queryBuilder: undefined,
  queryBuilderPanelVisible: false,
  queryFields: [],
  searchResultCache: {},
  sortBy: DEFAULT_TABLE_SORT_COLUMN_ORDER,
  selectedFilters: [],
  defaultFilters: [],
  savedSearch: undefined,
  queryFilters: undefined,
  initialValues: {
    columns: [],
    queryBuilder: undefined,
    selectedFilters: [],
    queryFilters: [],
    searchText: undefined,
  },
  ...persistedState,
};

export default createSlice({
  name,
  initialState,
  selectors: {
    selectColumnOrder: (state: SearchV3Slice) => state.columnOrder,
    selectSelectedFilters: (state: SearchV3Slice) => state.selectedFilters,
    selectActiveFilters: createSelector(
      (state: SearchV3Slice) => state.selectedFilters,
      (selectedFilters) => selectedFilters.filter(isActiveFilter),
    ),
    selectFeaturedClauses: createSelector(
      (state: SearchV3Slice) => state.selectedFilters,
      (selectedFilters) =>
        selectedFilters
          .filter(isActiveFilter)
          .filter(
            (filter) =>
              filter.fieldId === 'clause' &&
              filter.operatorId === 'contains_any',
          )
          .map(
            (clauseFilter) => (clauseFilter.values[0] as any)?.provision ?? '',
          ),
    ),
    selectIsSearchDirty: (state: SearchV3Slice) => {
      if (!state.initialValues) return false;

      return !isEqual(
        {
          columns: state.initialValues.columns,
          queryBuilder: state.initialValues.queryBuilder,
          selectedFilters: state.initialValues.selectedFilters,
          searchText: state.initialValues.searchText,
        },
        {
          columns: state.columnOrder?.map(String),
          queryBuilder: state.queryBuilder,
          selectedFilters: state.selectedFilters,
          searchText: state.searchText!,
        },
      );
    },
    selectColumnWidths: (state: SearchV3Slice) => state.columnWidths,
    selectSortBy: (state: SearchV3Slice) => state.sortBy,
    selectFieldValues: (state: SearchV3Slice) => state.fieldValues,
    selectResultsCache: (state: SearchV3Slice) => state.searchResultCache,
    selectPage: (state: SearchV3Slice) => state.page,
    selectPageSize: (state: SearchV3Slice) => state.pageSize,
    selectSearchText: (state: SearchV3Slice) => state.searchText,
    selectQueryBuilder: (state: SearchV3Slice) => state.queryBuilder,
    selectQueryFilters: (state: SearchV3Slice) => state.queryFilters,
    selectQueryBuilderPanelVisible: (state: SearchV3Slice) =>
      state.queryBuilderPanelVisible,
    selectQueryFields: (state: SearchV3Slice) => state.queryFields,
    selectDefaultFilters: (state: SearchV3Slice) => state.defaultFilters,
    selectIsInitialized: (state: SearchV3Slice) =>
      state.searchText !== undefined,
    selectSavedSearch: (state: SearchV3Slice) => state.savedSearch,
    selectInitialValues: (state: SearchV3Slice) => state.initialValues,
  },
  reducers: {
    init: (state, action: { payload: InitData }) => {
      state.columnOrder = action.payload.columnOrder;
      state.pageSize = action.payload.pageSize;
      state.sortBy = action.payload.sortBy;

      state.initialValues = {
        columns: action.payload.columnOrder,
        queryBuilder: undefined,
        queryFilters: buildFilterQuery({
          filters: action.payload.selectedFilters ?? [],
        }),
        selectedFilters: action.payload.selectedFilters ?? [],
        searchText: undefined,
      };
    },
    initSavedSearchData: (state, action: { payload: InitSavedSearch }) => {
      const { searchFilters } = action.payload;
      const savedSearch = action.payload.savedSearch ?? state.savedSearch;

      if (savedSearch && testIsQueryV3(savedSearch)) {
        state.columnOrder = savedSearch.resultTable.map((column) =>
          column.filterId.toString(),
        );
        if (savedSearch.isComplexQuery) {
          state.queryBuilder = savedSearch.query;
          state.queryFilters = savedSearch.query;
        } else {
          state.selectedFilters = [
            ...toSearchV3Filters(savedSearch.query, searchFilters),
            ...(savedSearch.filters?.map((filter) => toFilter(filter)) ?? []),
          ];
          state.queryFilters = buildFilterQuery({
            filters: state.selectedFilters ?? [],
            searchFilters,
          });
        }

        state.initialValues = {
          columns: state.columnOrder,
          queryBuilder: state.queryBuilder,
          selectedFilters: state.selectedFilters,
          queryFilters: state.queryFilters,
          searchText: state.searchText,
        };
      }
    },
    setSavedSearch: (state, action: { payload: SavedSearch | undefined }) => {
      state.savedSearch = action.payload;
    },
    patchColumnWidths: (state, action: { payload: ColumnWidths }) => {
      state.columnWidths = {
        ...state.columnWidths,
        ...action.payload,
      };
    },
    patchSearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchDocumentItem[] },
    ) => {
      payload.forEach((item) => (state.searchResultCache[item.id] = item));
    },
    patchEvisearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchItem[] },
    ) => {
      payload.forEach(
        (item) =>
          (state.searchResultCache[item.id] = {
            ...item,
            document_handlers: get(
              item,
              'selected_field_values.document_handler_id_Document.value_structured',
              [],
            ),
            full_text_matches: item.highlights?.full_text_matches ?? [],
            // TODO: Map types correctly once the API gets formalized
            permissions: item.permissions as any,
            selected_field_values: item.selected_field_values as any,
            id: item.id as any,
          }),
      );
    },

    setColumnOrder: (state, action) => {
      state.columnOrder = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setFieldValues: (
      state,
      {
        payload,
      }: { payload: { fieldId: string; values: DropdownFieldValue[] } },
    ) => {
      state.fieldValues[payload.fieldId] = payload.values;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
    },
    setQueryBuilder: (state, action) => {
      state.queryBuilder = action.payload;
    },
    setQueryFilters: (state, action) => {
      state.queryFilters = action.payload;
    },
    setQueryBuilderPanelVisible: (state, action) => {
      state.queryBuilderPanelVisible = action.payload;
    },
    setQueryFields: (state, { payload }) => {
      state.queryFields = payload;
    },
    setDefaultFilters: (state, action) => {
      state.defaultFilters = action.payload;
    },
    setSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },
    setSortBy: (state, action) => {
      const newSortBy = { ...action.payload };
      state.sortBy = newSortBy;
    },
    reset: (state) => {
      return {
        ...initialState,
        ...pick(state, ['selectedFilters', 'searchResultCache']),
        filters: [],
        selectedFilters: [],
      };
    },
  },
});
